/**
 * Timezone utilities for handling organization-specific timezone conversions.
 * 
 * This utility ensures that when users schedule posts, tasks, or other time-sensitive
 * content, the times are properly converted to UTC for storage while respecting
 * the organization's timezone settings.
 */

import { format, parseISO } from 'date-fns';

/**
 * Maps organization timezone strings to display labels
 */
export const TIMEZONE_DISPLAY_MAP: Record<string, string> = {
  'America/New_York': 'Eastern Time (ET)',
  'America/Toronto': 'Eastern Time (ET)',
  'America/Mexico_City': 'Central Time (CT)',
  'America/Chicago': 'Central Time (CT)',
  'America/Winnipeg': 'Central Time (CT)',
  'America/Denver': 'Mountain Time (MT)',
  'America/Edmonton': 'Mountain Time (MT)',
  'America/Phoenix': 'Mountain Time (MT)',
  'America/Los_Angeles': 'Pacific Time (PT)',
  'America/Vancouver': 'Pacific Time (PT)',
  'America/Anchorage': 'Alaska Time (AKT)',
  'Pacific/Honolulu': 'Hawaii Time (HT)',
  'America/Tijuana': 'Pacific Time (PT)',
  'America/Mazatlan': 'Mountain Time (MT)',
  'America/Monterrey': 'Central Time (CT)',
};

/**
 * Get the organization's timezone from user/organization context.
 * This would typically come from user preferences or organization settings.
 */
export function getOrganizationTimezone(): string {
  // For now, we'll try to get it from localStorage or fall back to a default
  // In a real implementation, this would come from the user's organization settings
  const storedTimezone = localStorage.getItem('organizationTimezone');
  return storedTimezone || 'America/Chicago'; // Default fallback
}

/**
 * Format a UTC Date as a datetime-local input value string in the given timezone.
 * Uses native Intl API — no extra dependencies needed.
 *
 * @param date - UTC Date object
 * @param timezone - IANA timezone string (e.g. 'America/Chicago')
 * @returns String in 'YYYY-MM-DDTHH:mm' format suitable for datetime-local inputs
 */
export function formatForDatetimeLocalInput(date: Date, timezone: string): string {
  // sv-SE locale produces "YYYY-MM-DD HH:mm" — ideal for datetime-local
  const intl = new Intl.DateTimeFormat('sv-SE', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return intl.format(date).replace(' ', 'T');
}

/**
 * Parse a datetime-local input string (treated as the given timezone) to a UTC Date.
 * Uses native Intl API — no extra dependencies needed.
 *
 * @param localString - String in 'YYYY-MM-DDTHH:mm' format from a datetime-local input
 * @param timezone - IANA timezone string (e.g. 'America/Chicago')
 * @returns UTC Date object
 */
export function parseFromDatetimeLocalInput(localString: string, timezone: string): Date {
  const wallTime = new Date(localString + ':00Z').getTime();
  if (!Number.isFinite(wallTime)) return new Date(NaN);
  // Sample both sides of a DST change instead of assuming the offset at UTC
  // midnight also applies to the requested local time.
  const offsets = new Set([-86400000, 0, 86400000].map(delta => {
    const sample = wallTime + delta;
    const local = formatForDatetimeLocalInput(new Date(sample), timezone);
    return sample - new Date(local + ':00Z').getTime();
  }));
  const candidates = [...offsets].map(offset => new Date(wallTime + offset));
  const exact = candidates.filter(date => formatForDatetimeLocalInput(date, timezone) === localString);
  // Match native date input conventions: choose the earlier repeated time;
  // if a spring-forward time does not exist, advance it by the clock gap.
  if (exact.length) return new Date(Math.min(...exact.map(date => date.getTime())));
  const after = candidates.filter(date => formatForDatetimeLocalInput(date, timezone) > localString);
  return after.sort((a, b) => a.getTime() - b.getTime())[0] || new Date(NaN);
}

/**
 * Convert a local date/time input to UTC for storage, respecting organization timezone.
 * 
 * When a user enters "2024-01-15 10:00 AM" in the UI, this function treats that
 * as 10:00 AM in the organization's timezone and converts it to UTC for storage.
 * 
 * @param dateString - Date in YYYY-MM-DD format
 * @param timeString - Time in HH:MM format (24-hour)
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns UTC date string in ISO format
 */
export function convertToUTCForStorage(
  dateString: string, 
  timeString: string, 
  organizationTimezone?: string
): string {
  // For now, create a naive Date object and assume user's browser timezone matches org timezone
  // This is a simplified approach until we can add date-fns-tz dependency
  const localDateTime = new Date(`${dateString}T${timeString}`);
  
  // Convert to UTC
  return localDateTime.toISOString();
}

/**
 * Convert a UTC datetime string to organization timezone for display.
 * 
 * When displaying a scheduled time to users, this converts the stored UTC time
 * back to the organization's local timezone.
 * 
 * @param utcDateString - UTC date string from the backend
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns Date object in organization timezone
 */
export function convertFromUTCForDisplay(
  utcDateString: string, 
  organizationTimezone?: string
): Date {
  // For now, return the UTC date as-is and let browser handle timezone conversion
  // This is a simplified approach until we can add date-fns-tz dependency
  return parseISO(utcDateString);
}

/**
 * Format a UTC datetime string for display in organization timezone.
 * 
 * @param utcDateString - UTC date string from the backend
 * @param formatStr - date-fns format string (default: 'PPpp')
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns Formatted date string in organization timezone
 */
export function formatDateInOrgTimezone(
  utcDateString: string, 
  formatStr: string = 'PPpp', 
  organizationTimezone?: string
): string {
  // For now, use browser's local timezone formatting
  // This is a simplified approach until we can add date-fns-tz dependency
  const utcDate = parseISO(utcDateString);
  return format(utcDate, formatStr);
}

/**
 * Extract date and time components for form fields from a UTC datetime.
 * 
 * Used when editing scheduled items - converts stored UTC time back to
 * organization timezone for populating date/time input fields.
 * 
 * @param utcDateString - UTC date string from the backend
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns Object with date and time strings for form inputs
 */
export function extractDateTimeForForm(
  utcDateString: string,
  organizationTimezone?: string
): { date: string; time: string } {
  const utcDate = parseISO(utcDateString);
  
  return {
    date: format(utcDate, 'yyyy-MM-dd'),
    time: format(utcDate, 'HH:mm')
  };
}

/**
 * Check if a UTC datetime is in the past relative to organization timezone.
 * 
 * @param utcDateString - UTC date string from the backend
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns true if the datetime is in the past
 */
export function isInPast(
  utcDateString: string,
  organizationTimezone?: string
): boolean {
  const utcDate = parseISO(utcDateString);
  const now = new Date();
  
  return utcDate < now;
}

/**
 * Get the current time in organization timezone.
 * 
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns Current date/time in organization timezone
 */
export function getCurrentOrgTime(organizationTimezone?: string): Date {
  return new Date();
}

/**
 * Get timezone display name for the organization.
 * 
 * @param organizationTimezone - Organization's timezone (optional, will auto-detect)
 * @returns Human-readable timezone name
 */
export function getTimezoneDisplayName(organizationTimezone?: string): string {
  const timezone = organizationTimezone || getOrganizationTimezone();
  return TIMEZONE_DISPLAY_MAP[timezone] || timezone;
}

/**
 * Update the stored organization timezone preference.
 * This should be called when the user's organization timezone is loaded or updated.
 * 
 * @param timezone - The organization's timezone string
 */
export function setOrganizationTimezone(timezone: string): void {
  localStorage.setItem('organizationTimezone', timezone);
}